/**
 * LocalStorage Adapter
 *
 * Browser localStorage implementation of IStorageAdapter.
 * Stores data in the browser's localStorage with JSON serialization.
 *
 * Features:
 * - Synchronous operations (wrapped in Promises for API compatibility)
 * - Version-based optimistic locking
 * - Prefix-based key namespacing
 * - Quota handling
 *
 * Limitations:
 * - ~5-10MB storage limit (browser dependent)
 * - Single-user (no server sync)
 * - No cross-device sync
 *
 * @example
 * ```ts
 * const adapter = new LocalStorageAdapter<SurveyData>('survey');
 * const result = await adapter.save('survey-123', data);
 * ```
 */

import type {
  IStorageAdapter,
  Result,
  StoredData,
  StorageMetadata,
  SaveOptions,
  ListOptions,
  ConflictInfo,
} from './IStorageAdapter'
import { StorageError, StorageErrorCode } from './IStorageAdapter'

export class LocalStorageAdapter<T> implements IStorageAdapter<T> {
  readonly type = 'local' as const
  private readonly keyPrefix: string

  /**
   * Create a new LocalStorageAdapter
   *
   * @param namespace Namespace prefix for all keys (e.g., 'survey', 'site')
   */
  constructor(namespace: string) {
    this.keyPrefix = `sc:${namespace}:`
  }

  /**
   * Get full storage key with namespace prefix
   */
  private getFullKey(key: string): string {
    return `${this.keyPrefix}${key}`
  }

  /**
   * Parse stored JSON data safely
   */
  private parseStoredData(jsonString: string): StoredData<T> | null {
    try {
      const parsed = JSON.parse(jsonString)

      // Validate structure
      if (!parsed.data || !parsed.metadata) {
        // Invalid data structure - return null for graceful handling
        return null
      }

      return parsed as StoredData<T>
    } catch {
      // Parse error - return null for graceful handling
      return null
    }
  }

  async get(key: string): Promise<Result<StoredData<T> | null>> {
    try {
      const fullKey = this.getFullKey(key)
      const item = localStorage.getItem(fullKey)

      if (!item) {
        return { success: true, data: null }
      }

      const storedData = this.parseStoredData(item)
      if (!storedData) {
        return {
          success: false,
          error: new StorageError('Invalid data format in storage', StorageErrorCode.INVALID_DATA),
        }
      }

      return { success: true, data: storedData }
    } catch (error) {
      return {
        success: false,
        error: new StorageError(`Failed to get item: ${key}`, StorageErrorCode.UNKNOWN, error),
      }
    }
  }

  async save(
    key: string,
    data: T,
    options: SaveOptions = {}
  ): Promise<Result<StoredData<T>, ConflictInfo<T>>> {
    try {
      const fullKey = this.getFullKey(key)

      // Check version conflict if expectedVersion provided
      if (options.expectedVersion !== undefined) {
        const existing = await this.get(key)

        if (existing.success && existing.data) {
          if (existing.data.metadata.version !== options.expectedVersion) {
            // Version conflict detected
            const conflict: ConflictInfo<T> = {
              currentVersion: existing.data.metadata.version,
              attemptedVersion: options.expectedVersion,
              currentData: existing.data.data,
              attemptedData: data,
            }

            return { success: false, error: conflict }
          }
        }
      }

      // Get existing data for version increment
      const existingResult = await this.get(key)
      const newVersion =
        existingResult.success && existingResult.data ? existingResult.data.metadata.version + 1 : 1

      // Create metadata
      const metadata: StorageMetadata = {
        id: key,
        version: newVersion,
        lastModified: new Date().toISOString(),
        ...(options.userId && { modifiedBy: options.userId }),
        ...(options.orgId && { orgId: options.orgId }),
      }

      const storedData: StoredData<T> = {
        data,
        metadata,
      }

      // Save to localStorage
      try {
        localStorage.setItem(fullKey, JSON.stringify(storedData))
      } catch (error) {
        // Handle quota exceeded
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          throw new StorageError('Storage quota exceeded', StorageErrorCode.QUOTA_EXCEEDED, error)
        }
        throw error
      }

      return { success: true, data: storedData }
    } catch (error) {
      if (error instanceof StorageError) {
        throw error
      }

      throw new StorageError(`Failed to save item: ${key}`, StorageErrorCode.UNKNOWN, error)
    }
  }

  async delete(key: string, options: SaveOptions = {}): Promise<Result<void, ConflictInfo<T>>> {
    try {
      const fullKey = this.getFullKey(key)

      // Check version conflict if expectedVersion provided
      if (options.expectedVersion !== undefined) {
        const existing = await this.get(key)

        if (existing.success && existing.data) {
          if (existing.data.metadata.version !== options.expectedVersion) {
            // Version conflict detected
            const conflict: ConflictInfo<T> = {
              currentVersion: existing.data.metadata.version,
              attemptedVersion: options.expectedVersion,
              currentData: existing.data.data,
              attemptedData: existing.data.data, // Same as current for delete
            }

            return { success: false, error: conflict }
          }
        }
      }

      localStorage.removeItem(fullKey)
      return { success: true, data: undefined }
    } catch (error) {
      throw new StorageError(`Failed to delete item: ${key}`, StorageErrorCode.UNKNOWN, error)
    }
  }

  async list(options: ListOptions = {}): Promise<Result<StorageMetadata[]>> {
    try {
      const metadata: StorageMetadata[] = []

      // Iterate through localStorage keys
      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i)
        if (!storageKey || !storageKey.startsWith(this.keyPrefix)) {
          continue
        }

        const item = localStorage.getItem(storageKey)
        if (!item) continue

        const storedData = this.parseStoredData(item)
        if (!storedData) continue

        // Apply filters
        if (options.orgId && storedData.metadata.orgId !== options.orgId) {
          continue
        }
        if (options.userId && storedData.metadata.modifiedBy !== options.userId) {
          continue
        }

        metadata.push(storedData.metadata)
      }

      // Sort if requested
      if (options.sortBy) {
        metadata.sort((a, b) => {
          const aVal = (a as unknown as Record<string, unknown>)[options.sortBy!]
          const bVal = (b as unknown as Record<string, unknown>)[options.sortBy!]

          if (typeof aVal === 'string' && typeof bVal === 'string') {
            const comparison = aVal.localeCompare(bVal)
            return options.sortDirection === 'desc' ? -comparison : comparison
          }

          if (typeof aVal === 'number' && typeof bVal === 'number') {
            const comparison = aVal - bVal
            return options.sortDirection === 'desc' ? -comparison : comparison
          }

          return 0
        })
      }

      // Apply pagination
      const start = options.offset || 0
      const end = options.limit ? start + options.limit : undefined
      const paginatedMetadata = end ? metadata.slice(start, end) : metadata.slice(start)

      return { success: true, data: paginatedMetadata }
    } catch (error) {
      return {
        success: false,
        error: new StorageError('Failed to list items', StorageErrorCode.UNKNOWN, error),
      }
    }
  }

  async exists(key: string): Promise<Result<boolean>> {
    try {
      const fullKey = this.getFullKey(key)
      const exists = localStorage.getItem(fullKey) !== null
      return { success: true, data: exists }
    } catch (error) {
      return {
        success: false,
        error: new StorageError(
          `Failed to check existence: ${key}`,
          StorageErrorCode.UNKNOWN,
          error
        ),
      }
    }
  }

  async clear(options: { orgId?: string } = {}): Promise<Result<void>> {
    try {
      // Collect keys to delete
      const keysToDelete: string[] = []

      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i)
        if (!storageKey || !storageKey.startsWith(this.keyPrefix)) {
          continue
        }

        // If orgId filter specified, check metadata
        if (options.orgId) {
          const item = localStorage.getItem(storageKey)
          if (!item) continue

          const storedData = this.parseStoredData(item)
          if (!storedData || storedData.metadata.orgId !== options.orgId) {
            continue
          }
        }

        keysToDelete.push(storageKey)
      }

      // Delete collected keys
      keysToDelete.forEach(key => localStorage.removeItem(key))

      return { success: true, data: undefined }
    } catch (error) {
      return {
        success: false,
        error: new StorageError('Failed to clear storage', StorageErrorCode.UNKNOWN, error),
      }
    }
  }

  async isAvailable(): Promise<boolean> {
    try {
      const testKey = `${this.keyPrefix}__test__`
      localStorage.setItem(testKey, 'test')
      localStorage.removeItem(testKey)
      return true
    } catch {
      return false
    }
  }
}
